'use client';

import { useBankAccountFieldsConfig } from '@commenda/api/bankAccount';
import {
  Badge,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { useEffect, useMemo } from 'react';
import { getAllCountries } from '../../config/country-bank-utils';
import { COMMENDA_OS_DATA_SOURCE } from '../../constants';
import type { CommendaOSFieldConfig, DataSourceConfig, FormDSL } from '@commenda/types';
import { ContextBindingEditor } from './ContextBindingEditor';
import {
  getContextSourceFields,
  getEntityContextKeys,
} from './context-helpers';

export function BankAccountEditor({
  props,
  dataSource,
  onUpdateProps,
  onDataSourceChange,
  currentFieldId,
  dsl,
}: {
  props: Record<string, unknown>;
  dataSource?: DataSourceConfig;
  onUpdateProps: (key: string, value: unknown) => void;
  onDataSourceChange: (dataSource: DataSourceConfig | undefined) => void;
  currentFieldId?: string;
  dsl?: FormDSL;
}) {
  const { config: fieldsConfig } = useBankAccountFieldsConfig();
  const allCountries = useMemo(
    () => (fieldsConfig ? getAllCountries(fieldsConfig) : []),
    [fieldsConfig]
  );

  const mode = (props?.mode as 'read' | 'read-write') || 'read-write';
  const defaultCountry = (props?.defaultCountry as string | null) ?? null;
  const bankCountryScopeMode =
    (props?.bankCountryScope as { mode?: string })?.mode || 'any';
  const commendaOS = dataSource?.commendaOS;

  const contextSourceFields = useMemo(() => {
    if (!dsl) return [];
    return getContextSourceFields(dsl, currentFieldId, 'corporation');
  }, [dsl, currentFieldId]);

  const formContextKeys = useMemo(() => {
    const requirements = (
      dsl as unknown as {
        form?: { contextRequirements?: { required?: string[] } };
      }
    )?.form?.contextRequirements;
    return requirements?.required || [];
  }, [dsl]);

  useEffect(() => {
    if (!dataSource || dataSource.type !== COMMENDA_OS_DATA_SOURCE) {
      onDataSourceChange({
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'bankAccount',
          contextBindings: [],
        },
      });
    }
  }, [dataSource, onDataSourceChange]);

  const updateCommendaOS = (updates: Partial<CommendaOSFieldConfig>) => {
    onDataSourceChange({
      type: COMMENDA_OS_DATA_SOURCE,
      commendaOS: {
        entity: 'bankAccount',
        contextBindings: commendaOS?.contextBindings,
        ...updates,
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="txt-small-medium text-text-secondary">
          Bank Account Configuration
        </h3>
        <Badge variant="gray">Commenda OS</Badge>
      </div>

      <div className="space-y-4 pl-4 border-l-2 border-border-secondary">
        {/* Mode Selection */}
        <div className="space-y-2">
          <Label>Mode</Label>
          <Select
            value={mode}
            onValueChange={(value) => onUpdateProps('mode', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="read">Read Only</SelectItem>
              <SelectItem value="read-write">Read &amp; Write</SelectItem>
            </SelectContent>
          </Select>
          <p className="txt-xs-regular text-text-tertiary">
            Read Only: Display masked account details. Read &amp; Write: Collect
            bank account information.
          </p>
        </div>

        {/* Bank account country scope */}
        <div className="space-y-2">
          <Label>Bank account country</Label>
          <Select
            value={bankCountryScopeMode}
            onValueChange={(value) =>
              onUpdateProps('bankCountryScope', { mode: value })
            }
          >
            <SelectTrigger className="text-left">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Accept from any country</SelectItem>
              <SelectItem value="locked">
                Lock to entity&apos;s incorporation country
              </SelectItem>
              <SelectItem value="locked-default">
                Lock to default country
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="txt-xs-regular text-text-tertiary">
            {bankCountryScopeMode === 'locked'
              ? "Country is fixed to the entity's incorporation country."
              : bankCountryScopeMode === 'locked-default'
                ? 'Country is fixed to the default country configured below.'
                : 'Form-filler selects from all supported countries.'}
          </p>
        </div>

        {bankCountryScopeMode !== 'locked' && (
          <div className="space-y-2">
            <Label>Default Country</Label>
            <Select
              value={defaultCountry ?? '__none__'}
              onValueChange={(value) =>
                onUpdateProps(
                  'defaultCountry',
                  value === '__none__' ? null : value
                )
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select country..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  None (use entity&apos;s incorporation country)
                </SelectItem>
                {allCountries.length > 0 ? (
                  allCountries.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-2 text-sm text-text-tertiary text-center">
                    Loading countries...
                  </div>
                )}
              </SelectContent>
            </Select>
            <p className="txt-xs-regular text-text-tertiary">
              {bankCountryScopeMode === 'locked-default'
                ? 'The country the form will be locked to.'
                : "Pre-selects the country when the add form opens. When unset, defaults to the entity's incorporation country."}
            </p>
          </div>
        )}

        {/* Context binding - corporationId */}
        <ContextBindingEditor
          entity="corporation"
          contextKeys={getEntityContextKeys('corporation')}
          contextBindings={commendaOS?.contextBindings}
          availableFields={contextSourceFields}
          formContextKeys={formContextKeys}
          onChange={(bindings) =>
            updateCommendaOS({ contextBindings: bindings })
          }
        />
      </div>
    </div>
  );
}
